import React, { useState, useEffect } from 'react';
import { Users, Building2, CreditCard, Activity, Plus, RefreshCw, Search, Filter } from 'lucide-react';
import TenantList from './TenantList';
import TenantForm from './TenantForm';
import TenantDetail from './TenantDetail';

const AdminDashboard = ({ api }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch admin stats and tenants on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsResponse, tenantsResponse] = await Promise.all([
        api.getAdminStats(),
        api.listTenants(statusFilter || null)
      ]);
      setStats(statsResponse);
      setTenants(tenantsResponse.tenants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (tenantData) => {
    try {
      await api.createTenant(tenantData);
      setShowCreateForm(false);
      loadData();
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateTenant = async (tenantId, tenantData) => {
    try {
      await api.updateTenant(tenantId, tenantData);
      loadData();
      if (selectedTenant?.id === tenantId) {
        const updated = await api.getTenant(tenantId);
        setSelectedTenant(updated);
      }
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to suspend this tenant?')) {
      return;
    }
    try {
      await api.deleteTenant(tenantId);
      loadData();
      if (selectedTenant?.tenant?.id === tenantId) {
        setSelectedTenant(null);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleAssignLicense = async (tenantId, licenseData) => {
    try {
      await api.assignLicense(tenantId, licenseData);
      loadData();
      if (selectedTenant?.tenant?.id === tenantId) {
        const updated = await api.getTenant(tenantId);
        setSelectedTenant(updated);
      }
    } catch (err) {
      throw err;
    }
  };

  const handleSelectTenant = async (tenant) => {
    try {
      const details = await api.getTenant(tenant.id);
      setSelectedTenant(details);
      setActiveTab('details');
    } catch (err) {
      alert(`Error loading tenant: ${err.message}`);
    }
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = !searchQuery ||
      tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tenant.billing_email && tenant.billing_email.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = !statusFilter || tenant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading && !stats) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">
          <RefreshCw className="spinning" size={32} />
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="admin-error">
          <p>Error loading admin data: {error}</p>
          <button onClick={loadData} className="admin-btn admin-btn-primary">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <Building2 size={32} color="#667eea" />
          <div>
            <h1>Admin Dashboard</h1>
            <p>Manage tenants, licenses, and users</p>
          </div>
        </div>
        <div className="admin-header-actions">
          <button
            onClick={loadData}
            className="admin-btn admin-btn-secondary"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="admin-btn admin-btn-primary"
          >
            <Plus size={16} />
            Create Tenant
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <Building2 size={24} color="white" />
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-number">{stats.total_tenants}</span>
              <span className="admin-stat-label">Total Tenants</span>
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' }}>
              <Activity size={24} color="white" />
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-number">{stats.active_tenants}</span>
              <span className="admin-stat-label">Active Tenants</span>
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)' }}>
              <CreditCard size={24} color="white" />
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-number">{stats.license_distribution.professional + stats.license_distribution.enterprise}</span>
              <span className="admin-stat-label">Paid Licenses</span>
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)' }}>
              <Users size={24} color="white" />
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-number">{stats.license_distribution.free}</span>
              <span className="admin-stat-label">Free Tier</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => { setActiveTab('overview'); setSelectedTenant(null); }}
        >
          <Building2 size={18} />
          Tenants
        </button>
        {selectedTenant && (
          <button
            className={`admin-tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            <Users size={18} />
            {selectedTenant.tenant?.name || 'Tenant Details'}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="admin-content">
        {activeTab === 'overview' && (
          <>
            {/* Search and Filter */}
            <div className="admin-toolbar">
              <div className="admin-search">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="admin-filter">
                <Filter size={18} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="trial">Trial</option>
                </select>
              </div>
            </div>

            {/* Tenant List */}
            <TenantList
              tenants={filteredTenants}
              onSelect={handleSelectTenant}
              onDelete={handleDeleteTenant}
            />
          </>
        )}

        {activeTab === 'details' && selectedTenant && (
          <TenantDetail
            tenant={selectedTenant.tenant}
            users={selectedTenant.users}
            licenses={selectedTenant.all_licenses}
            activeLicense={selectedTenant.active_license}
            userCount={selectedTenant.user_count}
            api={api}
            onUpdate={handleUpdateTenant}
            onAssignLicense={handleAssignLicense}
            onBack={() => { setActiveTab('overview'); setSelectedTenant(null); }}
            onRefresh={async () => {
              const details = await api.getTenant(selectedTenant.tenant.id);
              setSelectedTenant(details);
            }}
          />
        )}
      </div>

      {/* Create Tenant Modal */}
      {showCreateForm && (
        <TenantForm
          onSubmit={handleCreateTenant}
          onClose={() => setShowCreateForm(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
